import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { useEffect, useState } from 'react'
import { getCampaignDesignations } from 'redux/services/designation'
import { getGroups } from 'redux/services/group'
import { ScrollableListData } from 'modules/ui/molecules/ScrollableList'
import useConfig from 'modules/config/state/use-config'
import { filter } from 'lodash'

interface Group {
  id: number
  name: string
  sequence: number
}

export interface Designation {
  id: number
  name: string
  groupId: number
  createdAt: string
  isDefaultDesignation: boolean
}

const DESIGNATION_SORT: Record<string, string> = {
  'A-Z': 'name:asc',
  'NEW-OLD': 'created_at:desc',
  'OLD-NEW': 'created_at:asc',
}

const useDesignationSearch = (campaignId: string, query: string, pageNumber: number) => {
  const config = useConfig()

  const [data, setData] = useState<ScrollableListData[]>([])
  const [loadingDesignations, setLoadingDesignations] = useState(true)
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [designations, setDesignations] = useState<Designation[] | null>(null)
  const [groups, setGroups] = useState<Group[] | null>(null)
  const [groupsWithDesignations, setGroupsWithDesignations] = useState<object[] | null>(null)

  const dispatch = useDispatch<TypedDispatch<RootState>>()

  useEffect(() => {
    setData([])
  }, [query])

  useEffect(() => {
    const fetchData = async () => {
      setLoadingDesignations(true)

      const sortDesignationBy = config.designation.sortDesignationBy
      const sort = DESIGNATION_SORT[sortDesignationBy]

      const apiParams = {
        per_page: 100,
        page: pageNumber,
        sort,
        ...(query ? { filter: `name*=${query}` } : {}),
      }

      const designationsResponse = await dispatch(
        getCampaignDesignations.initiate({
          campaignId,
          params: apiParams,
        }),
      ).unwrap()

      const designations = designationsResponse.data.map((designation) => ({
        id: designation.id,
        groupId: designation.designation_group_id,
        name: designation.name,
        createdAt: designation.created_at,
        isDefaultDesignation: designation.is_display_default,
      }))

      setDesignations(designations)
      setHasMore(designationsResponse.to < designationsResponse.total)

      setLoadingDesignations(false)
    }

    fetchData()
  }, [query, pageNumber, dispatch, campaignId, config.designation.sortDesignationBy])

  useEffect(() => {
    const fetchData = async () => {
      setLoadingGroups(true)

      const groupsResponse = await dispatch(
        getGroups.initiate({
          campaignId,
          params: {
            per_page: 100,
            page: 1,
            sort: 'sequence:asc',
          },
        }),
      ).unwrap()

      const groups = groupsResponse.data.map((group) => ({
        id: group.id,
        name: group.name,
        sequence: group.sequence,
      }))

      setGroups(groups)

      setLoadingGroups(false)
    }

    fetchData()
  }, [dispatch, campaignId, config.designation])

  useEffect(() => {
    // For each group, find the designations that belong to that group
    // APIv2 handles primary sorting (alphabetical, date), we just prioritize default designation
    const groupsWithDesignations = (groups ?? []).map((group: any) => {
      const groupWithDesignation = group
      const designationsInGroup = filter(designations, { groupId: group.id })

      // Sort to prioritize default designation, keeping APIv2's sort order otherwise
      const sortedDesignations = designationsInGroup.slice().sort((a, b) => {
        // Put default designation first, preserve APIv2 order for others
        if (a.isDefaultDesignation && !b.isDefaultDesignation) return -1
        if (!a.isDefaultDesignation && b.isDefaultDesignation) return 1
        return 0 // Keep relative order from APIv2 sort
      })

      groupWithDesignation.designations = sortedDesignations

      return groupWithDesignation
    })

    setGroupsWithDesignations(groupsWithDesignations)
  }, [designations, groups])

  useEffect(() => {
    const newData: ScrollableListData[] = []

    groupsWithDesignations?.forEach((groupWithDesignations: any) => {
      const designations = groupWithDesignations.designations.map((designation: any) => {
        return {
          label: designation.name,
          value: designation.id,
        }
      })

      // Add header and designations to newdata if there are designations in the group
      if (designations.length > 0) {
        if (config.designation.showDesignationGroupName) {
          const header = {
            label: groupWithDesignations.name,
            value: null,
            isHeader: true,
          }

          newData.push(header)
        }

        newData.push(...designations)
      }
    })

    setData(newData)
  }, [groupsWithDesignations, config.designation.showDesignationGroupName])

  useEffect(() => {
    setLoading(loadingDesignations || loadingGroups)
  }, [loadingDesignations, loadingGroups])

  const defaultDesignation = (designations || []).find(
    (d) => d.id === config.designation.designationId,
  )

  return { data, error: false, loading, hasMore, defaultDesignation, designations }
}

export default useDesignationSearch
